import React, { createContext, useCallback, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Users, Briefcase, Stethoscope, Building2, ChevronRight, Sparkles } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

/**
 * Unified registration modal. Any CTA in the app that wants to send the user
 * to registration should use `useRegisterModal().open()` instead of linking
 * to a specific /register/* URL. The modal shows 4 large tiles and routes each
 * one to /register/account with the correct ?kind= so the profile creation
 * step is pre-configured.
 */
const RegisterModalContext = createContext(null);

const OPTIONS = [
  {
    key: "client",
    Icon: Users,
    color: "bg-orange",
    tint: "bg-orange/10",
    href: "/register/account",
  },
  {
    key: "business",
    Icon: Briefcase,
    color: "bg-teal-deep",
    tint: "bg-teal-deep/10",
    href: "/register/account?kind=business",
  },
  {
    key: "professional",
    Icon: Stethoscope,
    color: "bg-teal",
    tint: "bg-teal/10",
    href: "/register/account?kind=professional",
  },
  {
    key: "organization",
    Icon: Building2,
    color: "bg-violet-600",
    tint: "bg-violet-600/10",
    href: "/register/account?kind=ngo",
  },
];

export const RegisterModalProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const openModal = useCallback(() => setOpen(true), []);
  const closeModal = useCallback(() => setOpen(false), []);
  return (
    <RegisterModalContext.Provider value={{ open: openModal, close: closeModal, isOpen: open }}>
      {children}
      {open && <RegisterModal onClose={closeModal} />}
    </RegisterModalContext.Provider>
  );
};

export const useRegisterModal = () => useContext(RegisterModalContext) || { open: () => {}, close: () => {}, isOpen: false };

const RegisterModal = ({ onClose }) => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const r = t.registerModal || {};

  const go = (href) => {
    onClose();
    navigate(href);
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="register-modal"
    >
      <div
        className="bg-cream w-full md:max-w-3xl rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative p-6 md:p-8 border-b border-gray-200 bg-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-cream hover:bg-gray-200 flex items-center justify-center transition-colors"
            data-testid="register-modal-close"
            aria-label="Close"
          >
            <X size={18} />
          </button>
          <p className="eyebrow text-orange inline-flex items-center gap-2">
            <Sparkles size={12} /> {r.eyebrow || "Únete a la RED"}
          </p>
          <h2 className="font-display text-3xl md:text-4xl text-teal-deep leading-tight mt-2 pr-10">
            {r.title || "¿Cómo quieres unirte?"}
          </h2>
          <p className="text-sm text-teal-soft mt-2 max-w-lg">
            {r.subtitle || "Elige la opción que mejor te describe. Podrás agregar más perfiles después."}
          </p>
        </div>

        <div className="p-6 md:p-8 overflow-y-auto space-y-3">
          {OPTIONS.map(({ key, Icon, color, tint, href }) => (
            <button
              key={key}
              type="button"
              onClick={() => go(href)}
              className="group w-full text-left bg-white hover:bg-white rounded-2xl border-2 border-gray-200 hover:border-orange p-4 md:p-5 flex items-center gap-4 transition-all shadow-sm hover:shadow-lg"
              data-testid={`register-modal-option-${key}`}
            >
              <span className={`w-14 h-14 rounded-2xl ${tint} flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform`}>
                <span className={`w-10 h-10 rounded-xl ${color} text-white flex items-center justify-center`}>
                  <Icon size={20} />
                </span>
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl text-teal-deep leading-tight">
                  {(r.options && r.options[key]?.title) || key}
                </div>
                <div className="text-sm text-teal-soft mt-1 leading-snug">
                  {(r.options && r.options[key]?.desc) || ""}
                </div>
              </div>
              <ChevronRight size={20} className="text-orange shrink-0 group-hover:translate-x-1 transition-transform" />
            </button>
          ))}
        </div>

        <div className="px-6 md:px-8 pb-6 md:pb-8 text-center">
          <p className="text-xs text-teal-soft">
            {r.footer || "¿Ya tienes cuenta?"}{" "}
            <button
              type="button"
              onClick={() => go("/login")}
              className="text-orange font-bold hover:underline"
              data-testid="register-modal-login-link"
            >
              {r.footerLink || "Inicia sesión"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterModal;
